import { format } from "date-fns";
import { de } from "date-fns/locale";

import type {
	ProjectRevenueInvoice,
	ProjectRevenueMonth,
	ProjectRevenueReport,
} from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";

// Flip to true to add each invoice's travel cost into the invoiced total and the percentage.
// The backend headline total (totalInvoicedCents) is the subtotal only, travel cost excluded.
export const INCLUDE_TRAVEL_COST_IN_INVOICED = false;

export function getInvoiceAmountCents(invoice: ProjectRevenueInvoice): number {
	if (!INCLUDE_TRAVEL_COST_IN_INVOICED) {
		return invoice.subTotalCents;
	}
	return invoice.subTotalCents + invoice.travelCostCentsBeforeVatCents;
}

export function getInvoicedTotalCents(report: ProjectRevenueReport): number {
	if (!INCLUDE_TRAVEL_COST_IN_INVOICED) {
		return report.totalInvoicedCents;
	}
	const travelCostCents = report.invoices.reduce(
		(sum, invoice) => sum + invoice.travelCostCentsBeforeVatCents,
		0,
	);
	return report.totalInvoicedCents + travelCostCents;
}

// "YYYY-MM-DD" -> "YYYY-MM"
export function getMonthKeyFromDate(isoDate: string): string {
	return isoDate.slice(0, 7);
}

export type ProjectRevenueTimelineRow = {
	month: string; // "YYYY-MM"
	monthData: ProjectRevenueMonth | null;
	invoices: ProjectRevenueInvoice[];
};

// Build one row per month, from the union of the tracked months and the invoice
// delivery-start months. Latest month first.
export function buildTimelineRows(report: ProjectRevenueReport): ProjectRevenueTimelineRow[] {
	const monthDataByKey = new Map<string, ProjectRevenueMonth>();
	report.monthlyBreakdown.forEach((month) => monthDataByKey.set(month.month, month));

	const invoicesByMonth = new Map<string, ProjectRevenueInvoice[]>();
	report.invoices.forEach((invoice) => {
		const monthKey = getMonthKeyFromDate(invoice.deliveryStartDate);
		const monthInvoices = invoicesByMonth.get(monthKey) ?? [];
		monthInvoices.push(invoice);
		invoicesByMonth.set(monthKey, monthInvoices);
	});

	const monthKeys = new Set<string>([...monthDataByKey.keys(), ...invoicesByMonth.keys()]);

	return [...monthKeys]
		.sort((a, b) => b.localeCompare(a))
		.map((month) => ({
			month,
			monthData: monthDataByKey.get(month) ?? null,
			invoices: invoicesByMonth.get(month) ?? [],
		}));
}

// "YYYY-MM" -> "September 2025"
export function formatMonthLabel(monthKey: string): string {
	return format(new Date(`${monthKey}-01T00:00:00`), "MMMM yyyy", { locale: de });
}
